/**
 * V5.3: 인사관리 > 공휴일관리 페이지
 *
 * **기능:**
 * - 공휴일 목록 조회 (회사별/연도별)
 * - 공휴일 추가/수정/삭제
 * - 국가 공휴일 시드 데이터 생성
 * - 국가 공휴일 동기화 (KR: 공공데이터포털 API, CN: JSON 리소스)
 * - 동기화 로그 조회 (자동/수동 동기화 이력)
 *
 * **V5.3 변경:**
 * - Tailwind 클래스 제거, CSS 모듈로 전환
 * - 실제로 적용되는 스타일로 운영툴 수준 UI 구현
 *
 * **권한:**
 * - system_admin: 전체 (KR/CN 모두)
 * - role_admin_kr: KR만
 * - role_admin_cn: CN만
 * - role_hr: 자신의 companyCode만
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AlertTriangle, Calendar } from 'lucide-react';

// Hooks
import {
  useHolidays,
  useHolidayMutations,
  useHolidaySyncLogs,
  Holiday,
  HolidayType,
  CompanyCode,
} from '../../shared/hooks/useHolidays';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';

// Components
import {
  HolidayToolbar,
  HolidayTable,
  HolidayCalendarView,
  HolidayFormModal,
  SyncLogsPanel,
  ConfirmModal,
  ViewMode,
  HolidayFilters,
  SortConfig,
  SortField,
  DEFAULT_FILTERS,
  DEFAULT_SORT,
  CURRENT_YEAR,
  HOLIDAY_TYPE_LABELS,
} from './components/holidays';

// Styles
import styles from './components/holidays/holidaysStyles.module.css';

// ============================================================================
// 메인 컴포넌트
// ============================================================================

export function HrHolidaysPage() {
  // ========== 상태 ==========
  // V5.12.1: 초기값 null로 변경 - accessibleCompanies 계산 전 API 호출 방지
  const [companyCode, setCompanyCode] = useState<CompanyCode | null>(null);
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<HolidayFilters>(DEFAULT_FILTERS);
  const [sortConfig, setSortConfig] = useState<SortConfig>(DEFAULT_SORT);
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // 모달 상태
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<Holiday | null>(null);
  const [showSeedConfirm, setShowSeedConfirm] = useState(false);
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);
  const [showCopyConfirm, setShowCopyConfirm] = useState(false); // V5.12: 작년 복사

  // ========== 훅 ==========
  const { user } = useCurrentUser();
  const { holidays, isLoading, error, refetch } = useHolidays(companyCode, selectedYear);
  const {
    upsertHoliday,
    archiveHoliday,
    seedNationalHolidays,
    syncNationalHolidaysKR,
    syncNationalHolidaysCN,
    copyHolidaysFromYear, // V5.12
    isLoading: isMutating,
  } = useHolidayMutations();
  const {
    logs: syncLogs,
    isLoading: isLoadingLogs,
    isForbidden: syncLogsForbidden, // V5.12.2: 권한 없음 상태
    refetch: refetchLogs,
  } = useHolidaySyncLogs(companyCode, 10);

  // ========== 권한 처리 ==========
  const userRoles = user?.roles || [];
  const isSystemAdmin = userRoles.includes('system_admin');
  const isKrAdmin = userRoles.includes('role_admin_kr');
  const isCnAdmin = userRoles.includes('role_admin_cn');
  const isHr = userRoles.includes('role_hr');
  const isRoleExec = userRoles.includes('role_exec'); // V60: 임원 읽기 전용
  const userCompanyCode = (user?.profile as any)?.companyCode as CompanyCode | undefined;

  // 접근 가능한 회사 목록 (V60: role_exec 읽기 전용 추가)
  const accessibleCompanies = useMemo<CompanyCode[]>(() => {
    if (isSystemAdmin) return ['KR', 'CN'];
    if (isKrAdmin) return ['KR'];
    if (isCnAdmin) return ['CN'];
    if ((isHr || isRoleExec) && userCompanyCode) return [userCompanyCode];
    return [];
  }, [isSystemAdmin, isKrAdmin, isCnAdmin, isHr, isRoleExec, userCompanyCode]);

  // V60: role_exec는 쓰기 버튼 숨김
  const canWriteHolidays = isSystemAdmin || isKrAdmin || isCnAdmin || isHr;

  // V5.12.1: 초기 companyCode 설정 - null이거나 권한 없으면 첫 번째 허용 회사로 설정
  useEffect(() => {
    if (accessibleCompanies.length > 0) {
      if (companyCode === null || !accessibleCompanies.includes(companyCode)) {
        setCompanyCode(accessibleCompanies[0]);
      }
    }
  }, [accessibleCompanies, companyCode]);

  // ========== 통계 계산 ==========
  const stats = useMemo(() => {
    const total = holidays.length;
    const national = holidays.filter((h) => h.type === 'national').length;
    const company = holidays.filter((h) => h.type === 'company').length;
    const temporary = holidays.filter((h) => h.type === 'temporary').length;
    return { total, national, company, temporary };
  }, [holidays]);

  // ========== 필터링 & 검색 ==========
  const filteredHolidays = useMemo(() => {
    return holidays.filter((h) => {
      // 유형 필터
      if (filters.type !== 'all' && h.type !== filters.type) {
        return false;
      }

      // 검색어 필터
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesName = h.name.toLowerCase().includes(query);
        const matchesDate = h.date.includes(query);
        const matchesType = HOLIDAY_TYPE_LABELS[h.type].toLowerCase().includes(query);
        if (!matchesName && !matchesDate && !matchesType) {
          return false;
        }
      }

      return true;
    });
  }, [holidays, filters, searchQuery]);

  // ========== 핸들러 ==========

  // 정렬 변경
  const handleSortChange = useCallback((field: SortField) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  // 공휴일 저장
  const handleSave = async (data: { date: string; name: string; type: HolidayType }) => {
    if (!companyCode) return; // V5.12.1: null guard
    try {
      await upsertHoliday({
        companyCode,
        date: data.date,
        name: data.name,
        type: data.type,
        holidayId: editingHoliday?.id, // V238: 수정 모드 시 기존 문서 ID 전달
      });
      setShowFormModal(false);
      setEditingHoliday(null);
      refetch();
    } catch (err) {
      console.error('공휴일 저장 실패:', err);
      alert('공휴일 저장에 실패했습니다.');
    }
  };

  // 공휴일 삭제
  const handleDelete = async (holiday: Holiday) => {
    try {
      await archiveHoliday(holiday.id);
      setShowDeleteConfirm(null);
      refetch();
    } catch (err) {
      console.error('공휴일 삭제 실패:', err);
      alert('공휴일 삭제에 실패했습니다.');
    }
  };

  // 시드 생성
  const handleSeed = async () => {
    if (!companyCode) return; // V5.12.1: null guard
    try {
      const result = await seedNationalHolidays({
        companyCode,
        fromYear: selectedYear,
        toYear: selectedYear + 9,
      });
      setShowSeedConfirm(false);
      alert(`시드 완료: ${result.createdCount}개 생성, ${result.skippedCount}개 중복 스킵`);
      refetch();
    } catch (err) {
      console.error('시드 실패:', err);
      alert('시드에 실패했습니다.');
    }
  };

  // 동기화
  const handleSync = async () => {
    try {
      const syncFn = companyCode === 'KR' ? syncNationalHolidaysKR : syncNationalHolidaysCN;
      const result = await syncFn(selectedYear);
      setShowSyncConfirm(false);
      alert(
        `동기화 완료 (${result.year}년 ${result.companyCode}):\n` +
          `- 생성: ${result.createdCount}개\n` +
          `- 업데이트: ${result.updatedCount}개\n` +
          `- 스킵: ${result.skippedCount}개`
      );
      refetch();
      refetchLogs();
    } catch (err: any) {
      console.error('동기화 실패:', err);
      const message = err?.message || '동기화에 실패했습니다.';
      alert(message);
    }
  };

  // V5.12: 작년 공휴일 복사 (CN)
  const handleCopyFromLastYear = async () => {
    if (!companyCode) return; // V5.12.1: null guard
    try {
      const result = await copyHolidaysFromYear({
        companyCode,
        fromYear: selectedYear - 1,
        toYear: selectedYear,
      });
      setShowCopyConfirm(false);
      alert(
        `복사 완료 (${result.fromYear}년 → ${result.toYear}년 ${result.companyCode}):\n` +
          `- 생성: ${result.createdCount}개\n` +
          `- 업데이트: ${result.updatedCount}개\n` +
          `- 스킵: ${result.skippedCount}개`
      );
      refetch();
      refetchLogs();
    } catch (err: any) {
      console.error('복사 실패:', err);
      const message = err?.message || '복사에 실패했습니다.';
      alert(message);
    }
  };

  // 수정 버튼 클릭
  const handleEdit = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setShowFormModal(true);
  };

  // 추가 버튼 클릭
  const handleAdd = () => {
    setEditingHoliday(null);
    setShowFormModal(true);
  };

  // 캘린더에서 공휴일 클릭
  const handleHolidayClick = (holiday: Holiday) => {
    handleEdit(holiday);
  };

  // ========== 렌더링 ==========

  // 권한 없음
  if (accessibleCompanies.length === 0) {
    return (
      <div className={styles.noPermission}>
        <h1 className={styles.noPermissionTitle}>
          인사관리 &gt; 공휴일관리
        </h1>
        <div className={styles.noPermissionAlert}>
          <AlertTriangle />
          <p>공휴일 관리 권한이 없습니다.</p>
        </div>
      </div>
    );
  }

  // V5.12.2: companyCode null일 때 주요 컴포넌트만 조건부 렌더링

  return (
    <div className={styles.pageContainer}>
      {/* 헤더 + 통계 카드 */}
      <div className={styles.headerCard}>
        <div className={styles.headerContent}>
          {/* 제목 영역 */}
          <div>
            <div className={styles.headerTitle}>
              <Calendar />
              <h1>공휴일 관리</h1>
            </div>
            <p className={styles.headerSubtitle}>
              {companyCode ? `${companyCode === 'KR' ? '한국' : '중국'} · ` : ''}
              {selectedYear}년 법정공휴일 및 회사 휴일을 관리합니다
            </p>
          </div>

          {/* 통계 카드 */}
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statValue}>{stats.total}</div>
              <div className={styles.statLabel}>전체</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statValueRed}>{stats.national}</div>
              <div className={styles.statLabel}>법정공휴일</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statValueBlue}>{stats.company}</div>
              <div className={styles.statLabel}>회사행사</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statValueGray}>{stats.temporary}</div>
              <div className={styles.statLabel}>임시휴일</div>
            </div>
          </div>
        </div>
      </div>

      {/* 안내 문구 */}
      <div className={styles.warningBanner}>
        <AlertTriangle />
        <div>
          <strong>주의:</strong> 휴일 등록은 근로관리(출근차단) 및 휴일근무 정책에 영향을
          줍니다.
        </div>
      </div>

      {/* 툴바 - V5.12.2: companyCode가 있을 때만 렌더링 */}
      {companyCode && (
        <div className={styles.cardWithPadding}>
          <HolidayToolbar
            companyCode={companyCode}
            accessibleCompanies={accessibleCompanies}
            selectedYear={selectedYear}
            onCompanyChange={setCompanyCode}
            onYearChange={setSelectedYear}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            filters={filters}
            onFiltersChange={setFilters}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onRefresh={refetch}
            onSync={canWriteHolidays ? () => setShowSyncConfirm(true) : undefined}
            onCopyFromLastYear={canWriteHolidays ? () => setShowCopyConfirm(true) : undefined}
            onAdd={canWriteHolidays ? handleAdd : undefined}
            isLoading={isLoading}
            isMutating={isMutating}
          />
        </div>
      )}

      {/* 에러 표시 */}
      {error && (
        <div className={styles.errorBanner}>
          <AlertTriangle />
          {error.message}
        </div>
      )}

      {/* 메인 컨텐츠: 목록 또는 캘린더 */}
      <div className={styles.cardOverflowHidden}>
        {viewMode === 'list' ? (
          <HolidayTable
            holidays={filteredHolidays}
            isLoading={isLoading}
            sortConfig={sortConfig}
            onSortChange={handleSortChange}
            onEdit={canWriteHolidays ? handleEdit : undefined}
            onDelete={canWriteHolidays ? (holiday) => setShowDeleteConfirm(holiday) : undefined}
          />
        ) : (
          <HolidayCalendarView
            holidays={filteredHolidays}
            selectedYear={selectedYear}
            isLoading={isLoading}
            onHolidayClick={canWriteHolidays ? handleHolidayClick : undefined}
          />
        )}
      </div>

      {/* 동기화 로그 카드 - V5.12.2: companyCode null이면 렌더링 스킵 */}
      {companyCode && (
        <div className={styles.cardWithPadding}>
          <SyncLogsPanel
            logs={syncLogs}
            isLoading={isLoadingLogs}
            companyCode={companyCode}
            onRefresh={refetchLogs}
            isForbidden={syncLogsForbidden}
          />
        </div>
      )}

      {/* ========== 모달 ========== */}

      {/* 추가/수정 모달 */}
      {showFormModal && (
        <HolidayFormModal
          holiday={editingHoliday}
          onSave={handleSave}
          onClose={() => {
            setShowFormModal(false);
            setEditingHoliday(null);
          }}
          isLoading={isMutating}
        />
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteConfirm && (
        <ConfirmModal
          title="공휴일 삭제"
          message={
            showDeleteConfirm.type === 'national'
              ? `"${showDeleteConfirm.name}" (${showDeleteConfirm.date})은(는) 법정공휴일입니다.\n\n삭제하면 보관(Archive) 처리되며, 동기화 시 스킵됩니다.\n정말 삭제하시겠습니까?`
              : `"${showDeleteConfirm.name}" (${showDeleteConfirm.date})을(를) 삭제하시겠습니까?`
          }
          confirmLabel="삭제"
          confirmColor="red"
          icon="trash"
          onConfirm={() => handleDelete(showDeleteConfirm)}
          onCancel={() => setShowDeleteConfirm(null)}
          isLoading={isMutating}
        />
      )}

      {/* 시드 확인 모달 */}
      {showSeedConfirm && (
        <ConfirmModal
          title="국가 공휴일 시드 생성"
          message={`${companyCode} 지역의 ${selectedYear}~${selectedYear + 9}년 국가 공휴일을 생성하시겠습니까?\n\n이미 존재하는 공휴일은 건너뜁니다.`}
          confirmLabel="생성"
          confirmColor="green"
          icon="calendar"
          onConfirm={handleSeed}
          onCancel={() => setShowSeedConfirm(false)}
          isLoading={isMutating}
        />
      )}

      {/* 동기화 확인 모달 */}
      {showSyncConfirm && (
        <ConfirmModal
          title="국가 공휴일 동기화"
          message={
            companyCode === 'KR'
              ? `${selectedYear}년 한국 법정공휴일을 공공데이터포털 API에서 동기화하시겠습니까?\n\n- 새로운 공휴일: 추가\n- 이름 변경: 업데이트\n- 삭제된 공휴일: 유지 (archived 상태는 스킵)`
              : `${selectedYear}년 중국 공휴일을 JSON 리소스에서 동기화하시겠습니까?\n\n- 새로운 공휴일: 추가\n- 이름 변경: 업데이트\n- 삭제된 공휴일: 유지 (archived 상태는 스킵)`
          }
          confirmLabel="동기화"
          confirmColor="purple"
          icon="download"
          onConfirm={handleSync}
          onCancel={() => setShowSyncConfirm(false)}
          isLoading={isMutating}
        />
      )}

      {/* V5.12: 작년 복사 확인 모달 */}
      {showCopyConfirm && (
        <ConfirmModal
          title="작년 공휴일 복사"
          message={`${selectedYear - 1}년 ${companyCode === 'KR' ? '한국' : '중국'} 공휴일을 ${selectedYear}년으로 복사하시겠습니까?\n\n- 새로운 공휴일: 추가\n- 이름 변경: 업데이트\n- 이미 존재: 스킵\n- 삭제된 공휴일: 유지 (archived 상태는 스킵)`}
          confirmLabel="복사"
          confirmColor="green"
          icon="calendar"
          onConfirm={handleCopyFromLastYear}
          onCancel={() => setShowCopyConfirm(false)}
          isLoading={isMutating}
        />
      )}
    </div>
  );
}
